/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, useEffect } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import AuthMainLayout from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout';
import Link from 'components/ChoreoSystem/Link/Link';
import UnderMaintenanceBanner from 'components/UnderMaintenanceBanner/UnderMaintenanceBanner';
import {
  emailLoginConfigs,
  fidpGitHub,
  fidpGoogle,
  fidpMicrosoft,
  enableAsgardeoMaintenanceFlag,
  supportCommunity,
  PRODUCT_NAME,
} from 'config';
import SignupCard from 'modules/signup/SignupCard';
import PreLoader from 'pages/preloader/PreLoader';
import { isChrome, isFirefox, setCookie } from 'utils/browser';
import { getGrowthHackingParams } from 'utils/growthHacking/utm';
import logger from 'utils/logger';
import generateUUID from 'utils/uuid';

export const IS_FROM_CONTEST_PAGE = 'isFromContestPage';

interface LocationState {
  returnToUrl: string;
  returnToOrg: string;
  displayOrgName: string;
}

function Signup() {
  const history = useHistory();
  const { state, search } = useLocation<LocationState>();
  const returnToUrl = state?.returnToUrl || '/';
  const returnToOrg = state?.returnToOrg || '';
  const searchParamsFromReturnToUrl = state?.returnToUrl.replace(/^\/?\?/, '');
  const isSupportedEngine = isChrome() || isFirefox();
  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState(
    new URLSearchParams(searchParamsFromReturnToUrl || search)
  );
  const { signIn } = useAuthContext();
  const [isMobile, setIsMobile] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const isFromContestPage = params.get('isFromContestPage');
  if (isFromContestPage) {
    setCookie(IS_FROM_CONTEST_PAGE, isFromContestPage);
  }

  const registrationUnavailableBannerDescription = () => (
    <FormattedMessage
      id="src.pages.RegisterOrganization.RegistrationUnavailableBanner.description"
      defaultMessage="
        Thank you for your interest in {productName}. We're currently performing some essential maintenance
        to improve our platform.{newLine}New registrations are temporarily unavailable during this period.
        We apologize for the inconvenience and ask that you please try again shortly.{newLine} {supportLink}"
      values={{
        productName: PRODUCT_NAME,
        newLine: (
          <div>
            <br />
          </div>
        ),
        supportLink: (
          <Link
            href={supportCommunity}
            color="primary"
            testId="visit-support-link"
          >
            <FormattedMessage
              id="src.components.UserExceedBanner.description.link"
              defaultMessage="Contact Support"
            />
          </Link>
        ),
      }}
    />
  );

  const handleSignUp = async (
    fidp: string,
    stateParam: { [key: string]: string },
    captchaInfo?: string,
    username?: string
  ) => {
    let encodedStateParam = '';
    const modifiedStateParams = { ...stateParam };
    const growthHackingParams = getGrowthHackingParams([...queryParams]);
    const hasGrowthHackingParams = growthHackingParams.length > 0;
    if (hasGrowthHackingParams) {
      growthHackingParams.forEach(([key, value]) => {
        modifiedStateParams[key] = value;
      });
    }
    encodedStateParam = btoa(JSON.stringify(modifiedStateParams));

    try {
      setIsLoading(true);
      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };
      signIn(
        {
          fidp,
          state: encodedStateParam,
          ...(username && { username }),
          ...(captchaInfo && { captchaInfo }),
        },
        undefined,
        undefined,
        undefined,
        undefined,
        tokenRequestConfig
      );
    } catch (signInError) {
      logger.error(signInError);
      history.push('/internal-error');
      setIsLoading(false);
    }
  };

  const storeUtmParamsIfNeeded = () => {
    const growthHackingParams = getGrowthHackingParams([...queryParams]);
    if (growthHackingParams.length > 0) {
      localStorage.setItem('utm-params', returnToUrl);
    }
  };

  const handleEmailSignup = () => {
    storeUtmParamsIfNeeded();
    const emailSignupUrl =
      `${emailLoginConfigs.ASGARDEO_SIGNUP_URL}?initiated-platform=choreo&template-name=` +
      `${emailLoginConfigs.REGISTER_MAIL_TEMPLATE_NAME}&tenant-url-prefix=${emailLoginConfigs.TENANT_URL_PREFIX}` +
      `&post-register-callback=${emailLoginConfigs.POST_REGISTER_CALLBACK_URL}`;
    window.location.href = emailSignupUrl;
  };

  const handleGithubSignup = () => {
    const stateParamObj = {
      fidpId: fidpGitHub,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpGitHub, stateParamObj);
  };

  const handleGoogleSignup = () => {
    const stateParamObj = {
      fidpId: fidpGoogle,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpGoogle, stateParamObj);
  };

  const handleMicrosoftSignup = () => {
    const stateParamObj = {
      fidpId: fidpMicrosoft,
      returnToUrl,
      returnToOrg,
    };
    handleSignUp(fidpMicrosoft, stateParamObj);
  };

  useEffect(() => {
    const searchParams = state?.returnToUrl.replace(/^\/?\?/, '');
    const qParams = new URLSearchParams(searchParams || search);
    setQueryParams(qParams);
  }, [state, search]);

  const handleResize = () => {
    if (window.innerWidth < 1000) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return <PreLoader />;
  }

  if (enableAsgardeoMaintenanceFlag) {
    return (
      <UnderMaintenanceBanner
        isOpen={true}
        title={
          <FormattedMessage
            defaultMessage="Registration Temporarily Paused"
            id="pages.signup.registrationTemporarilyPaused.title"
          />
        }
        description={registrationUnavailableBannerDescription()}
      />
    );
  }

  return (
    <AuthMainLayout isMobile={isMobile} isSupportedEngine={isSupportedEngine}>
      <SignupCard
        onEmailClick={handleEmailSignup}
        onGithubClick={handleGithubSignup}
        onGoogleClick={handleGoogleSignup}
        onMicrosoftClick={handleMicrosoftSignup}
        isMobile={isMobile}
      />
    </AuthMainLayout>
  );
}

export default Signup;
